'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { ArrowRight, ExternalLink, Scale, BookCheck, ShieldAlert, Network, Bot, CheckCircle, Gavel } from 'lucide-react'
import Link from 'next/link'

export default function RegulationsPage() {

  const regulations = [
    {
      icon: Scale,
      title: "Federal Acquisition Regulation (FAR)",
      description: "The primary set of rules governing the U.S. federal government's procurement process. Covers everything from acquisition planning to contract administration."
    },
    {
      icon: ShieldAlert,
      title: "Defense Supplement (DFARS)",
      description: "Specific regulations applicable to contracts with the Department of Defense (DoD), often including stringent cybersecurity requirements (like NIST 800-171)."
    },
    {
      icon: BookCheck,
      title: "Cybersecurity (CMMC/NIST)",
      description: "Frameworks like CMMC and standards like NIST SP 800-171 mandate specific cybersecurity practices for handling Controlled Unclassified Information (CUI)."
    },
    {
      icon: Network,
      title: "State & Local Variations",
      description: "Each state and locality has its own procurement laws, codes, and compliance requirements, adding layers of complexity."
    }
  ]

  return (
    <div className="container max-w-7xl mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="text-center mb-20">
        <Gavel className="h-16 w-16 text-primary mx-auto mb-6" />
        <h1 className="text-4xl lg:text-5xl font-bold mb-4">
          Navigating Government Regulations
        </h1>
        <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
          Understanding and complying with the complex web of federal, state, and local procurement regulations is crucial for success.
        </p>
      </div>

      {/* Why Compliance Matters */}
      <section className="mb-16 bg-muted/50 rounded-lg p-8 lg:p-12">
        <h2 className="text-3xl font-bold text-center mb-8">The Importance of Compliance</h2>
        <div className="grid md:grid-cols-2 gap-8 text-center">
          <div>
             <ShieldAlert className="h-10 w-10 text-red-500 mx-auto mb-4" />
             <h3 className="text-xl font-semibold mb-2">Mitigate Risks</h3>
             <p className="text-muted-foreground">
               Non-compliance can lead to contract termination, fines, suspension, debarment, and reputational damage.
             </p>
          </div>
           <div>
             <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-4" />
             <h3 className="text-xl font-semibold mb-2">Build Trust & Win Contracts</h3>
             <p className="text-muted-foreground">
               Demonstrating robust compliance builds trust with agencies and primes, increasing your win probability.
             </p>
          </div>
        </div>
      </section>

      {/* Key Regulatory Areas */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-center mb-12">Key Regulatory Areas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {regulations.map((reg) => (
            <Card key={reg.title} className="h-full">
              <CardHeader className="flex flex-row items-center gap-4 pb-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <reg.icon className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-xl">{reg.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{reg.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* How We Help Section */}
      <section className="mb-20 text-center">
         <div className="flex justify-center mb-4">
            <Bot className="h-10 w-10 text-primary" />
         </div>
        <h2 className="text-3xl font-bold mb-4">How GovCon Data Helps</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
          Our platform integrates compliance considerations. Leverage AI to analyze solicitation requirements against regulations and utilize our resources to stay informed.
        </p>
        {/* Optional: Add specific feature links here if applicable */}
      </section>
      
      {/* Disclaimer */} 
      <section className="mb-16 text-center border-t pt-8">
         <p className="text-sm text-muted-foreground max-w-3xl mx-auto">
           <strong>Disclaimer:</strong> GovCon Data provides informational tools and resources. The content on this platform does not constitute legal advice. Users are responsible for ensuring their own compliance with all applicable laws and regulations and should consult with qualified legal counsel for specific advice.
         </p>
      </section>

      {/* Call to Action */}
      <div className="text-center space-y-6 bg-primary/5 rounded-lg p-10">
        <h2 className="text-3xl font-bold">Simplify Your Compliance Journey</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Explore how our platform features can assist you in navigating the complex regulatory landscape.
        </p>
        <div className="flex gap-4 justify-center pt-4">
          <Button asChild size="lg">
            <Link href="/pricing">
              View Plans
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="text-primary border-primary hover:bg-primary/5">
            <Link href="/contact">
              Contact Us
            </Link>
          </Button>
        </div>
      </div>

    </div>
  )
} 